import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Users, Plus, X, Loader } from 'lucide-react';
import { accounts } from '../../services/apiService';
import { Loader as SectionLoader } from '../UI/LoadingSpinner';
import ModalPortal from '../UI/ModalPortal';
import { Autocomplete } from '@anyemp/ui-kit';
import { useAuth } from '../../hooks/useAuth';
import toast from 'react-hot-toast';
import SocialAccountAccordionItem from '../UI/SocialAccountAccordionItem';

/**
 * Компонент для отображения social accounts, где талант назначен как Assigned User
 * Используется в TalentDetail для candidate, employee, presale и в MyProfile для self view
 */
const AssignedSocialAccountsTab = ({
  userId,
  talentType,
  socialAccountsList: externalSocialAccountsList,
  loading: externalLoading,
  error: externalError,
  selfMode = false,
}) => {
  const { hasPermission, user } = useAuth();
  const queryClient = useQueryClient();
  const [expandedAccountId, setExpandedAccountId] = useState(null);
  const [notifOverrides, setNotifOverrides] = useState({});

  const canToggleNotif  = selfMode || hasPermission('account-service', 'social_account', 'update');
  const canViewNotes    = selfMode || hasPermission('account-service', 'social_account_note', 'list');
  const canAddNotes     = selfMode || hasPermission('account-service', 'social_account_note', 'create');
  const canEditNotes    = selfMode || hasPermission('account-service', 'social_account_note', 'update');
  const canDeleteNotes  = selfMode || hasPermission('account-service', 'social_account_note', 'delete');

  const handleNotificationsChange = (id, val) =>
    setNotifOverrides(prev => ({ ...prev, [id]: val }));

  // Состояние для назначения social accounts
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [availableSocialAccounts, setAvailableSocialAccounts] = useState([]);
  const [selectedSocialAccountIds, setSelectedSocialAccountIds] = useState([]);
  const [loadingAvailableSocialAccounts, setLoadingAvailableSocialAccounts] = useState(false);
  const [attachingSocialAccounts, setAttachingSocialAccounts] = useState(false);

  // Use external data if provided, otherwise fetch internally
  const isExternalData = externalSocialAccountsList !== undefined;

  // Fetch social accounts by assigned user ID (only if no external data)
  const {
    data: socialAccountsResponse,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['assignedSocialAccounts', userId],
    queryFn: () => accounts.getSocialAccountsByAssignedUserId(userId),
    retry: 1,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    staleTime: 0,
    enabled: !!userId && !isExternalData  // Disable if external data provided
  });

  // Use external or internal data
  const assignedSocialAccounts = isExternalData
    ? (externalSocialAccountsList || [])
    : (socialAccountsResponse?.data?.data || socialAccountsResponse?.data || []);

  const displayLoading = isExternalData ? (externalLoading ?? false) : isLoading;
  const displayError = isExternalData ? externalError : (isError ? error?.message : null);

  // Загрузка списка всех доступных social accounts
  const fetchAvailableSocialAccounts = async () => {
    try {
      setLoadingAvailableSocialAccounts(true);
      const response = await accounts.getAllSocialAccounts({ all: true });

      if (response.success && response.data) {
        // Исключаем уже назначенные social accounts
        const assignedIds = assignedSocialAccounts.map(acc => acc.id);
        const available = Array.isArray(response.data)
          ? response.data.filter(acc => !assignedIds.includes(acc.id))
          : [];
        setAvailableSocialAccounts(available);
      } else {
        toast.error(response.error || 'Не удалось загрузить доступные social accounts');
      }
    } catch (err) {
      console.error('Ошибка загрузки доступных social accounts:', err);
      toast.error('Ошибка загрузки доступных social accounts');
    } finally {
      setLoadingAvailableSocialAccounts(false);
    }
  };

  // Открытие модального окна для назначения social accounts
  const handleOpenAssignModal = () => {
    setShowAssignModal(true);
    setSelectedSocialAccountIds([]);
    fetchAvailableSocialAccounts();
  };

  // Закрытие модального окна
  const handleCloseAssignModal = () => {
    setShowAssignModal(false);
    setSelectedSocialAccountIds([]);
    setAvailableSocialAccounts([]);
  };

  // Преобразование social accounts в формат для MultiSelect
  const getSocialAccountOptions = () => {
    return availableSocialAccounts.map(socialAccount => ({
      value: String(socialAccount.id),
      label: `${socialAccount.username || socialAccount.login || `Social Account #${socialAccount.id}`}${socialAccount.tool?.name ? ` (${socialAccount.tool.name})` : ''}${socialAccount.account?.name ? ` - ${socialAccount.account.name}` : ''}`
    }));
  };

  // Назначение выбранных social accounts
  const handleAttachSocialAccounts = async () => {
    if (selectedSocialAccountIds.length === 0) {
      toast.error('Пожалуйста, выберите хотя бы один social account');
      return;
    }

    try {
      setAttachingSocialAccounts(true);
      const response = await accounts.attachSocialAccountsToUser(userId, selectedSocialAccountIds);

      if (response.success) {
        toast.success(`Успешно назначено ${selectedSocialAccountIds.length} social account(s)`);
        handleCloseAssignModal();

        // Обновляем список social accounts
        queryClient.invalidateQueries(['assignedSocialAccounts', userId]);
      } else {
        toast.error(response.error || 'Не удалось назначить social accounts');
      }
    } catch (err) {
      console.error('Ошибка назначения social accounts:', err);
      toast.error('Ошибка назначения social accounts');
    } finally {
      setAttachingSocialAccounts(false);
    }
  };

  // Проверка прав на назначение social accounts
  const canAssignSocialAccounts = hasPermission('talent-service', `${talentType}_assigned_social_accounts`, 'create');

  if (displayLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <SectionLoader variant="component" size="md" fullHeight={false} />
      </div>
    );
  }

  if (displayError) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
        <p className="text-light-text-secondary dark:text-dark-text-secondary">
          {displayError || 'Failed to load assigned social accounts'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
            Assigned Social Accounts
          </h3>
          <span className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">
            {assignedSocialAccounts.length} account{assignedSocialAccounts.length !== 1 ? 's' : ''}
          </span>
        </div>
        {canAssignSocialAccounts && userId && !isExternalData && (
          <button
            onClick={handleOpenAssignModal}
            className="group inline-flex items-center px-4 py-2 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 dark:from-primary-500 dark:to-primary-600 dark:hover:from-primary-600 dark:hover:to-primary-700 text-white text-sm font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
          >
            <Plus className="h-4 w-4 mr-2 group-hover:rotate-90 transition-transform duration-300" />
            Assign Social Accounts
          </button>
        )}
      </div>

      {/* Empty State */}
      {assignedSocialAccounts.length === 0 && (
        <div className="text-center py-12 bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-lg border border-light-border-primary dark:border-dark-border-primary">
          <Users className="mx-auto h-12 w-12 text-light-text-tertiary dark:text-dark-text-tertiary mb-4" />
          <p className="text-light-text-secondary dark:text-dark-text-secondary text-lg mb-2">
            No assigned social accounts found
          </p>
          <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">
            This {talentType} is not assigned to any social accounts yet
          </p>
        </div>
      )}

      {/* Social Accounts List */}
      {assignedSocialAccounts.length > 0 && (
        <div className="space-y-3">
          {assignedSocialAccounts.map((socialAccount, index) => {
            const toolName = socialAccount.tool?.name;
            const accountData = {
              ...socialAccount,
              name: socialAccount.username || socialAccount.login,
              is_notifications: notifOverrides[socialAccount.id] !== undefined
                ? notifOverrides[socialAccount.id]
                : socialAccount.is_notifications,
              two_factor_enabled: undefined,
              is_hidden_from_employee: undefined,
              userAssignments: undefined,
              created_at: undefined,
              updated_at: undefined,
              tool: undefined,
            };
            return (
              <SocialAccountAccordionItem
                key={socialAccount.id}
                account={accountData}
                accountKey={socialAccount.id}
                isExpanded={expandedAccountId === socialAccount.id}
                onToggle={(key) => setExpandedAccountId(expandedAccountId === key ? null : key)}
                index={index}
                canViewPassword={true}
                selfMode={selfMode}
                canToggleNotifications={canToggleNotif}
                canViewNotes={canViewNotes}
                canAddNotes={canAddNotes}
                canEditNotes={canEditNotes}
                canDeleteNotes={canDeleteNotes}
                currentUserId={user?.id}
                onNotificationsChange={handleNotificationsChange}
                badges={toolName && (
                  <span className="hidden sm:inline-flex px-2.5 py-1 text-xs font-semibold rounded-lg bg-light-bg-tertiary dark:bg-dark-bg-primary text-light-text-secondary dark:text-dark-text-secondary border border-light-border-primary/50 dark:border-dark-border-primary/50">
                    {toolName}
                  </span>
                )}
              />
            );
          })}
        </div>
      )}

      {/* Modal для назначения social accounts */}
      {showAssignModal && (
        <ModalPortal onClose={attachingSocialAccounts ? undefined : handleCloseAssignModal}>
          <div className="flex max-h-[calc(100dvh-1.5rem)] w-full max-w-2xl min-w-0 flex-col overflow-hidden rounded-2xl border border-light-border-primary bg-light-bg-primary shadow-2xl animate-scale-in dark:border-dark-border-primary dark:bg-dark-bg-secondary sm:max-h-[calc(100dvh-4rem)]">
            {/* Декоративная полоса сверху */}
            <div className="h-1.5 bg-gradient-to-r from-primary-500 via-purple-500 to-pink-500"></div>

            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-light-border-primary bg-gradient-to-r p-4 dark:border-dark-border-primary sm:p-6 from-light-bg-primary to-light-bg-secondary dark:from-dark-bg-secondary dark:to-dark-bg-tertiary">
              <h3 className="text-xl font-bold text-light-text-primary dark:text-dark-text-primary flex items-center">
                <div className="p-2 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg mr-3 shadow-md">
                  <Plus className="h-5 w-5 text-white" />
                </div>
                Assign Social Accounts
              </h3>
              <button
                onClick={handleCloseAssignModal}
                className="p-2 rounded-lg text-light-text-tertiary dark:text-dark-text-tertiary hover:text-light-text-primary dark:hover:text-dark-text-primary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary transition-all duration-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 scrollbar-light">
              {loadingAvailableSocialAccounts ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="relative">
                    <div className="absolute inset-0 bg-primary-500/20 blur-xl rounded-full"></div>
                    <SectionLoader variant="component" size="md" fullHeight={false} />
                  </div>
                  <span className="mt-4 text-light-text-secondary dark:text-dark-text-secondary font-medium">
                    Загрузка доступных social accounts...
                  </span>
                </div>
              ) : availableSocialAccounts.length === 0 ? (
                <div className="text-center py-12">
                  <div className="relative inline-block mb-6">
                    <div className="absolute inset-0 bg-light-text-tertiary/10 dark:bg-dark-text-tertiary/10 blur-xl rounded-full"></div>
                    <AlertCircle className="relative mx-auto h-16 w-16 text-light-text-tertiary dark:text-dark-text-tertiary" />
                  </div>
                  <p className="text-lg font-semibold text-light-text-secondary dark:text-dark-text-secondary mb-2">
                    Нет доступных social accounts
                  </p>
                  <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">
                    Все social accounts уже назначены этому пользователю
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <Autocomplete
                    multiple
                    clearable
                    fullWidth
                    options={getSocialAccountOptions()}
                    value={selectedSocialAccountIds.map(String)}
                    onChange={(vals) => setSelectedSocialAccountIds((vals || []).map(Number))}
                    placeholder="Поиск и выбор social accounts..."
                    label="Выберите social accounts для назначения"
                  />
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex shrink-0 flex-col-reverse gap-3 border-t border-light-border-primary bg-light-bg-secondary p-4 pb-[max(1rem,env(safe-area-inset-bottom))] dark:border-dark-border-primary dark:bg-dark-bg-tertiary sm:flex-row sm:flex-wrap sm:items-center sm:justify-end sm:p-6">
              <button
                onClick={handleCloseAssignModal}
                disabled={attachingSocialAccounts}
                className="px-5 py-2.5 border-2 border-light-border-primary dark:border-dark-border-primary text-light-text-primary dark:text-dark-text-primary font-semibold rounded-xl hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-secondary hover:border-primary-300 dark:hover:border-primary-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Отмена
              </button>
              <button
                onClick={handleAttachSocialAccounts}
                disabled={attachingSocialAccounts || selectedSocialAccountIds.length === 0}
                className="px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 dark:from-primary-500 dark:to-primary-600 dark:hover:from-primary-600 dark:hover:to-primary-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {attachingSocialAccounts ? (
                  <span className="flex items-center">
                    <Loader className="h-4 w-4 mr-2 inline animate-spin" />
                    Назначение...
                  </span>
                ) : (
                  `Назначить ${selectedSocialAccountIds.length > 0 ? `(${selectedSocialAccountIds.length})` : ''}`
                )}
              </button>
            </div>
          </div>
        </ModalPortal>
      )}
    </div>
  );
};

export default AssignedSocialAccountsTab;
